import { Link, useParams, Navigate } from "react-router-dom";
import { FileText, ArrowLeft, ChevronRight, ScrollText, ShieldCheck, RotateCcw, XCircle, Tag, Cookie, Bot, KeyRound } from "lucide-react";
import Navbar from "@/components/xuma/Navbar";
import Footer from "@/components/xuma/Footer";
import { LEGAL_DOCS, getLegalDoc, LEGAL_UPDATED, LEGAL_EMAIL } from "@/data/legalContent";

const ICONS = {
  "terminos-y-condiciones": ScrollText,
  "aviso-de-privacidad": ShieldCheck,
  "reembolsos": RotateCcw,
  "cancelacion": XCircle,
  "promociones": Tag,
  "cookies": Cookie,
  "asistente": Bot,
  "derechos-arco": KeyRound,
};

export default function Legal() {
  const { slug } = useParams();

  return (
    <div className="min-h-screen bg-xuma-bg flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-5 py-12">
        {slug ? <LegalDoc slug={slug} /> : <LegalIndex />}
      </main>
      <Footer />
    </div>
  );
}

function LegalIndex() {
  return (
    <div data-testid="legal-index">
      <p className="text-xs uppercase tracking-[0.25em] text-xuma-primary font-medium">Centro Legal</p>
      <h1 className="mt-2 font-heading font-extrabold text-4xl sm:text-5xl text-xuma-dark">Centro Legal</h1>
      <p className="mt-4 max-w-2xl text-xuma-muted">
        Aquí puedes consultar las políticas, términos y avisos que regulan el uso de Xuma Social Business Card.
        Última actualización: {LEGAL_UPDATED}.
      </p>

      <div className="mt-10 grid gap-4 sm:grid-cols-2">
        {LEGAL_DOCS.map((d) => {
          const Icon = ICONS[d.slug] || FileText;
          return (
            <Link key={d.slug} to={`/legal/${d.slug}`} data-testid={`legal-link-${d.slug}`}
              className="group flex items-start gap-4 rounded-2xl bg-white border border-xuma-border p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all">
              <span className="h-11 w-11 rounded-xl bg-xuma-primary/10 grid place-items-center shrink-0">
                <Icon className="h-5 w-5 text-xuma-primary" />
              </span>
              <span className="flex-1">
                <span className="flex items-center justify-between">
                  <span className="font-heading font-bold text-xuma-dark">{d.title}</span>
                  <ChevronRight className="h-4 w-4 text-xuma-muted group-hover:text-xuma-primary transition-colors" />
                </span>
                <span className="block text-sm text-xuma-muted mt-1">{d.summary}</span>
              </span>
            </Link>
          );
        })}
      </div>

      <div className="mt-10 rounded-2xl bg-white border border-xuma-border p-6">
        <p className="font-heading font-bold text-xuma-dark">¿Dudas sobre estos documentos?</p>
        <p className="text-sm text-xuma-muted mt-1">
          Escríbenos a <a href={`mailto:${LEGAL_EMAIL}`} className="text-xuma-primary hover:underline">{LEGAL_EMAIL}</a>.
        </p>
      </div>
    </div>
  );
}

function LegalDoc({ slug }) {
  const doc = getLegalDoc(slug);
  if (!doc) return <Navigate to="/legal" replace />;

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-10" data-testid={`legal-doc-${slug}`}>
      <aside className="hidden lg:block">
        <Link to="/legal" className="inline-flex items-center gap-1.5 text-sm text-xuma-muted hover:text-xuma-dark mb-4">
          <ArrowLeft className="h-4 w-4" /> Centro Legal
        </Link>
        <nav className="space-y-1">
          {LEGAL_DOCS.map((d) => (
            <Link key={d.slug} to={`/legal/${d.slug}`}
              className={`block rounded-lg px-3 py-2 text-sm transition-colors ${d.slug === slug ? "bg-xuma-primary/10 text-xuma-primary font-medium" : "text-xuma-muted hover:bg-xuma-dash hover:text-xuma-dark"}`}>
              {d.title}
            </Link>
          ))}
        </nav>
      </aside>

      <article className="min-w-0">
        <Link to="/legal" className="lg:hidden inline-flex items-center gap-1.5 text-sm text-xuma-muted hover:text-xuma-dark mb-4">
          <ArrowLeft className="h-4 w-4" /> Centro Legal
        </Link>
        <p className="text-xs uppercase tracking-[0.25em] text-xuma-primary font-medium">Centro Legal</p>
        <h1 className="mt-2 font-heading font-extrabold text-3xl sm:text-4xl text-xuma-dark">{doc.title}</h1>
        <p className="mt-2 text-sm text-xuma-muted">Última actualización: {LEGAL_UPDATED}</p>

        <div className="mt-8 rounded-2xl bg-white border border-xuma-border p-6 sm:p-8 space-y-7">
          {doc.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-heading font-bold text-lg text-xuma-dark">{s.h}</h2>
              {s.text && <p className="mt-2 text-[15px] leading-relaxed text-xuma-text/80">{s.text}</p>}
              {s.items && (
                <ul className="mt-3 space-y-1.5">
                  {s.items.map((it, j) => (
                    <li key={j} className="flex gap-2 text-[15px] leading-relaxed text-xuma-text/80">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-xuma-primary shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </article>
    </div>
  );
}
